/**
 * @fileoverview Calendar Header
 *
 * Adapted from upstream:
 *  - View-switcher is now a controlled button-group that calls
 *    `setView` on the context (no `next/link`, no route navigation).
 *  - Added a `list` view option (sixth icon).
 *  - Added a category filter popover (Funnel icon) that lets the
 *    practitioner show / hide categories such as "reporting"
 *    (statutory lodgements) or "investigation windows" — the user
 *    preference for this control is held in the host page via
 *    `visibleCategories` + `onVisibleCategoriesChange`.
 *  - The "Add Event" button is replaced by a generic `+ Add key
 *    date` button that fires the page-level callback via
 *    `onDateCellClick(today)`.
 */

import type { ReactNode } from "react";
import {
  Plus,
  Filter,
  Check,
  Columns,
  Grid3x3,
  Grid2x2,
  List as ListIcon,
  CalendarRange,
  Rows3,
  Settings as SettingsIcon,
  type LucideIcon,
} from "lucide-react";

import type { CalendarDateMode } from "@/calendar/eventAdapter";

import { Button } from "@/ui/primitives/button";
import { Badge } from "@/ui/primitives/badge";
import { Checkbox } from "@/ui/primitives/checkbox";
import { Label } from "@/ui/primitives/label";
import { Toggle } from "@/ui/primitives/toggle";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/ui/composed/overlays/popover";

import { CATEGORY_META, CATEGORY_DISPLAY_ORDER } from "@/calendar/categoryMeta";

import { useCalendar } from "@/calendar/contexts/use-calendar";
import {
  DateNavigatorTitle,
  DateNavigatorNav,
} from "@/calendar/components/header/date-navigator";

import { cn } from "@/utils/misc";

import type { IEvent } from "@/calendar/interfaces";
import type { TCalendarView } from "@/calendar/types";
import type { KeyDateCategory } from "@cvx/keyDates/jobKeyDates/schema";

interface IProps {
  events: IEvent[];
  /** Currently visible categories (controlled by the host page). */
  visibleCategories?: ReadonlyArray<KeyDateCategory>;
  /** Setter for the visible categories — null/undefined hides the filter. */
  onVisibleCategoriesChange?: (next: KeyDateCategory[]) => void;
  /** Hide-subtasks toggle state (controlled by the host page, MOR-752). */
  hideSubtasks?: boolean;
  /** Setter for the toggle — undefined hides the control. */
  onHideSubtasksChange?: (next: boolean) => void;
  /** When true, hide the "+ Add key date" button (e.g. workspace view). */
  hideAddButton?: boolean;
  /**
   * When true, append a "Settings" tab to the view switcher that opens the
   * calendar-feed (ICS subscription) configuration. Workspace calendar only.
   */
  showSubscribe?: boolean;
  /**
   * Optional content rendered at the very top of the header card, above the
   * date navigator. The workspace calendar passes its page title +
   * description + job filter here so the heading lives inside the card.
   */
  headerSlot?: ReactNode;
}

const VIEW_OPTIONS: Array<{
  value: TCalendarView;
  label: string;
  icon: LucideIcon;
}> = [
  { value: "day", label: "Day", icon: ListIcon },
  { value: "week", label: "Week", icon: Columns },
  { value: "month", label: "Month", icon: Grid2x2 },
  { value: "year", label: "Year", icon: Grid3x3 },
  { value: "agenda", label: "Agenda", icon: CalendarRange },
  { value: "list", label: "List", icon: Rows3 },
];

const SETTINGS_OPTION: {
  value: TCalendarView;
  label: string;
  icon: LucideIcon;
} = {
  value: "settings",
  label: "Settings",
  icon: SettingsIcon,
};

const DATE_MODE_OPTIONS: Array<{ value: CalendarDateMode; label: string }> = [
  { value: "due", label: "Due" },
  { value: "planned", label: "Planned" },
  { value: "both", label: "Both" },
];

// Investigation windows live in a separate route (not on the calendar)
// so this filter only lists categories that actually surface as events.
const FILTERABLE_CATEGORIES = CATEGORY_DISPLAY_ORDER.filter(
  (category): category is Exclude<KeyDateCategory, "investigation_window"> =>
    category !== "investigation_window",
);

export function CalendarHeader({
  events,
  visibleCategories,
  onVisibleCategoriesChange,
  hideSubtasks,
  onHideSubtasksChange,
  hideAddButton,
  showSubscribe,
  headerSlot,
}: IProps) {
  const {
    view,
    setView,
    selectedDate,
    onDateCellClick,
    dateMode,
    setDateMode,
  } = useCalendar();

  const isSettings = view === "settings";
  // Append the Settings tab only on surfaces that own the feed (workspace).
  const viewOptions = showSubscribe
    ? [...VIEW_OPTIONS, SETTINGS_OPTION]
    : VIEW_OPTIONS;

  return (
    <div className="flex flex-col gap-3 border-b p-4">
      {headerSlot && <div className="border-b pb-3">{headerSlot}</div>}

      <DateNavigatorTitle events={events} />

      <div className="flex items-center gap-2">
        {!isSettings && <DateNavigatorNav />}

        <div className="ml-auto flex items-center gap-2">
          <div className="flex gap-1 overflow-x-auto rounded-lg bg-secondary/40 p-1">
            {viewOptions.map((option) => {
              const isActive = view === option.value;
              const Icon = option.icon;
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setView(option.value)}
                  className={cn(
                    "flex items-center gap-1.5 whitespace-nowrap rounded-md px-3 py-1.5 text-xs font-medium transition",
                    isActive
                      ? "bg-background text-primary shadow-sm"
                      : "text-primary/50 hover:text-primary/70",
                  )}
                >
                  <Icon className="size-3.5" />
                  {option.label}
                </button>
              );
            })}
          </div>

          {!isSettings && (
            <div
              className="flex gap-1 rounded-lg bg-secondary/40 p-1"
              role="group"
              aria-label="Show events by planned or due date"
            >
              {DATE_MODE_OPTIONS.map((option) => {
                const isActive = dateMode === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setDateMode(option.value)}
                    title={
                      option.value === "due"
                        ? "Place events on their due date"
                        : option.value === "planned"
                          ? "Place tasks on their planned window"
                          : "Show both planned and due markers"
                    }
                    className={cn(
                      "whitespace-nowrap rounded-md px-3 py-1.5 text-xs font-medium transition",
                      isActive
                        ? "bg-background text-primary shadow-sm"
                        : "text-primary/50 hover:text-primary/70",
                    )}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
          )}

          {!isSettings && visibleCategories && onVisibleCategoriesChange && (
            <CategoryFilter
              value={visibleCategories}
              onChange={onVisibleCategoriesChange}
            />
          )}

          {!isSettings && onHideSubtasksChange && (
            <Toggle
              size="sm"
              variant="outline"
              pressed={hideSubtasks ?? false}
              onPressedChange={(pressed) => onHideSubtasksChange(pressed)}
              className="h-9 data-[state=on]:bg-amber-100 data-[state=on]:text-amber-800 dark:data-[state=on]:bg-amber-950 dark:data-[state=on]:text-amber-300"
            >
              Hide subtasks
            </Toggle>
          )}

          {!isSettings && !hideAddButton && (
            <Button
              type="button"
              onClick={() => {
                const dateOnly = new Date(selectedDate);
                dateOnly.setHours(0, 0, 0, 0);
                onDateCellClick(dateOnly);
              }}
            >
              <Plus className="size-4" />
              Add new
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Category filter — popover with one checkbox per KeyDateCategory
// ---------------------------------------------------------------------------

function CategoryFilter({
  value,
  onChange,
}: {
  value: ReadonlyArray<KeyDateCategory>;
  onChange: (next: KeyDateCategory[]) => void;
}) {
  const valueSet = new Set(value);
  const allSelected = FILTERABLE_CATEGORIES.every((cat) => valueSet.has(cat));
  const someSelected = value.length > 0 && !allSelected;

  const toggle = (category: KeyDateCategory) => {
    if (valueSet.has(category)) {
      onChange(value.filter((c) => c !== category));
    } else {
      onChange([...value, category]);
    }
  };

  const toggleAll = () => {
    if (allSelected) {
      // Don't allow zero categories — that would render an empty
      // calendar and look like a bug. Fall back to "appointment" so
      // the user always sees at least the anchors.
      onChange(["appointment"]);
    } else {
      onChange([...FILTERABLE_CATEGORIES]);
    }
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="gap-2"
          aria-label="Filter key date categories"
        >
          <Filter className="size-4" />
          <span className="hidden sm:inline">Categories</span>
          {someSelected && (
            <Badge variant="secondary" className="text-[10px]">
              {value.length}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        <div className="border-b px-3 py-2">
          <button
            type="button"
            onClick={toggleAll}
            className="flex w-full items-center justify-between text-xs font-medium text-muted-foreground hover:text-foreground"
          >
            <span>{allSelected ? "Clear all" : "Show all"}</span>
            {allSelected && <Check className="size-3.5" />}
          </button>
        </div>
        <ul className="space-y-2 p-3">
          {FILTERABLE_CATEGORIES.map((category) => {
            const meta = CATEGORY_META[category];
            const Icon = meta.icon;
            const checked = valueSet.has(category);
            return (
              <li key={category} className="flex items-start gap-2">
                <Checkbox
                  id={`cat-${category}`}
                  checked={checked}
                  onCheckedChange={() => toggle(category)}
                  className="mt-0.5"
                />
                <div className="flex-1 space-y-0.5">
                  <Label
                    htmlFor={`cat-${category}`}
                    className="flex cursor-pointer items-center gap-1.5 text-sm font-medium leading-none"
                  >
                    {/* Colour swatch + icon so the user can map the
                        colour they see on the calendar back to the
                        category in one glance. */}
                    <span
                      className={cn(
                        "inline-flex size-5 shrink-0 items-center justify-center rounded",
                        meta.iconBgClass,
                      )}
                    >
                      <Icon className={cn("size-3", meta.iconFgClass)} />
                    </span>
                    {meta.label}
                  </Label>
                  <p className="pl-[26px] text-xs text-muted-foreground">
                    {meta.hint}
                  </p>
                </div>
              </li>
            );
          })}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
